import { useState, useEffect } from "react";

interface DevisFormProps {
  preselectedType?: string;
  page?: string;
}

export default function DevisForm({ preselectedType, page = "Tarifs" }: DevisFormProps) {
  const [focused, setFocused] = useState<string | null>(null);
  const [type, setType] = useState(preselectedType || "");

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.includes("type=volet")) setType("volet");
      if (hash.includes("type=store")) setType("store");
      if (hash.includes("type=moteur")) setType("moteur");
      if (hash.includes("type=motorisation")) setType("motorisation");
      if (hash.includes("type=reparation")) setType("reparation");
    };
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  return (
    <div className="min-w-0">
      <form
        id="tarifs-devis-form"
        action="https://formspree.io/f/xgoqgrbq"
        method="POST"
        className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border border-brand-border/80 shadow-[0_8px_32px_rgba(20,58,102,0.1)] overflow-hidden"
      >
        <input type="text" name="_gotcha" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
        <input type="hidden" name="_subject" value="Nouveau lead Dépan'Volets — Tarifs" />
        <input type="hidden" name="page" value={page} />
        <input type="hidden" name="_next" id="devis-form-next" value="/merci" />
        <script
          dangerouslySetInnerHTML={{
            __html: `document.getElementById('devis-form-next').value = window.location.origin + '/merci';`,
          }}
        />

        <div className="space-y-4 md:space-y-5">
          {/* Nom */}
          <div className="relative group">
            <label htmlFor="nom" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              Nom
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-user-line text-base md:text-lg"></i>
              </div>
              <input
                id="nom"
                name="nom"
                type="text"
                required
                onFocus={() => setFocused("nom")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text placeholder-brand-text/30 border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300"
                placeholder="Votre nom"
              />
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "nom" ? "w-full" : "w-0"}`} />
            </div>
          </div>

          {/* Email */}
          <div className="relative group">
            <label htmlFor="email" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              E-mail
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-mail-line text-base md:text-lg"></i>
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text placeholder-brand-text/30 border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300"
                placeholder="Votre e-mail"
              />
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "email" ? "w-full" : "w-0"}`} />
            </div>
          </div>

          {/* Téléphone */}
          <div className="relative group">
            <label htmlFor="telephone" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              Téléphone
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-phone-line text-base md:text-lg"></i>
              </div>
              <input
                id="telephone"
                name="telephone"
                type="tel"
                required
                onFocus={() => setFocused("telephone")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text placeholder-brand-text/30 border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300"
                placeholder="Votre numéro"
              />
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "telephone" ? "w-full" : "w-0"}`} />
            </div>
          </div>

          {/* Code postal */}
          <div className="relative group">
            <label htmlFor="code_postal" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              Code postal
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-map-pin-line text-base md:text-lg"></i>
              </div>
              <input
                id="code_postal"
                name="code_postal"
                type="text"
                required
                onFocus={() => setFocused("code_postal")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text placeholder-brand-text/30 border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300"
                placeholder="67000"
              />
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "code_postal" ? "w-full" : "w-0"}`} />
            </div>
          </div>

          {/* Type */}
          <div className="relative group">
            <label htmlFor="type" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              Type de prestation
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-tools-line text-base md:text-lg"></i>
              </div>
              <select
                id="type"
                name="type"
                required
                value={type}
                onChange={(e) => setType(e.target.value)}
                onFocus={() => setFocused("type")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300 appearance-none cursor-pointer"
              >
                <option value="" disabled>Choisissez une prestation</option>
                <option value="reparation">Réparation & dépannage de volet roulant</option>
                <option value="moteur">Remplacement du moteur de volet roulant</option>
                <option value="motorisation">Motorisation de volet roulant</option>
                <option value="store">Réparation & dépannage de store extérieur</option>
                <option value="store-moteur">Remplacement du moteur de store extérieur</option>
              </select>
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "type" ? "w-full" : "w-0"}`} />
              <div className="absolute right-3 md:right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-brand-text/30 pointer-events-none">
                <i className="ri-arrow-down-s-line text-base md:text-lg"></i>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="relative group">
            <label htmlFor="message" className="block text-xs font-semibold text-brand-text/70 uppercase tracking-wider mb-1.5 md:mb-2">
              Message (optionnel)
            </label>
            <div className="relative">
              <div className="absolute left-3 md:left-3.5 top-3 md:top-3.5 w-5 h-5 flex items-center justify-center text-brand-text/30">
                <i className="ri-message-3-line text-base md:text-lg"></i>
              </div>
              <textarea
                id="message"
                name="message"
                rows={3}
                maxLength={500}
                onFocus={() => setFocused("message")}
                onBlur={() => setFocused(null)}
                className="w-full bg-[#FAFBFC] rounded-lg md:rounded-xl pl-10 md:pl-11 pr-3 md:pr-4 py-3 md:py-3.5 text-sm text-brand-text placeholder-brand-text/30 border border-brand-border focus:border-brand-cyan focus:bg-white outline-none transition-all duration-300 resize-none"
                placeholder="Décrivez votre problème..."
              />
              <div className={`absolute bottom-0 left-0 h-[2px] bg-brand-cyan rounded-full transition-all duration-300 ${focused === "message" ? "w-full" : "w-0"}`} />
            </div>
            <p className="text-[10px] text-brand-text/40 mt-1 text-right">500 caractères maximum</p>
          </div>
        </div>

        {/* RGPD */}
        <div className="mt-4 md:mt-5 flex items-start gap-2.5 md:gap-3">
          <input
            type="checkbox"
            name="consentement"
            required
            id="consentement-tarifs"
            className="mt-0.5 md:mt-1 w-4 h-4 text-brand-cyan rounded border-brand-border focus:ring-brand-cyan cursor-pointer shrink-0"
          />
          <label htmlFor="consentement-tarifs" className="text-xs text-brand-text/70 leading-relaxed cursor-pointer">
            J'accepte que mes données soient utilisées pour être recontacté(e).{" "}
            <a href="/politique-confidentialite" className="text-brand-cyan underline hover:no-underline">
              Politique de confidentialité
            </a>
          </label>
        </div>

        {/* Bouton */}
        <button
          type="submit"
          className="w-full mt-5 md:mt-7 bg-brand-cyan text-white px-5 md:px-6 py-3.5 md:py-4 rounded-full font-semibold text-sm md:text-base hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_4px_20px_rgba(0,179,164,0.35)] transition-all duration-300 whitespace-nowrap overflow-hidden relative"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <i className="ri-send-plane-fill text-base md:text-lg"></i>
            Recevoir mon devis gratuit
          </span>
        </button>
      </form>
    </div>
  );
}